import React from 'react';
import { Link } from 'react-router-dom';
import { Ticket as TicketIcon } from 'lucide-react';

/**
 * One rule for every "Get Tickets" button on a game (Schedule page, homepage
 * Upcoming Games, Puck Drop), driven by the admin game form:
 *   - "Tickets Available" decides whether the button shows. Older game docs
 *     without the field fall back to home games only (the previous behavior).
 *   - "Ticket Link" decides where it goes: blank → the on-site /tickets store,
 *     a path → that route, a full URL → external box office in a new tab.
 */
export interface GameTicketFields {
  id?: string;
  isHome?: boolean;
  ticketsAvailable?: boolean;
  ticketLink?: string;
  opponent?: string;
}

export function getGameTicketTarget(game: GameTicketFields): { href: string; external: boolean } | null {
  const available = typeof game.ticketsAvailable === 'boolean' ? game.ticketsAvailable : !!game.isHome;
  if (!available) return null;
  const href = (game.ticketLink || '').trim() || '/tickets';
  return { href, external: /^https?:\/\//i.test(href) };
}

interface GetTicketsButtonProps {
  game: GameTicketFields;
  className?: string;
  label?: string;
  ariaLabel?: string;
}

export const GetTicketsButton: React.FC<GetTicketsButtonProps> = ({
  game,
  className = 'vb-btn vb-btn--primary vb-btn--sm',
  label = 'Get Tickets',
  ariaLabel,
}) => {
  const target = getGameTicketTarget(game);
  if (!target) return null;
  const content = (
    <>
      <TicketIcon size={16} aria-hidden="true" /> {label}
    </>
  );
  if (target.external) {
    return (
      <a href={target.href} target="_blank" rel="noopener noreferrer" className={className} aria-label={ariaLabel ? `${ariaLabel} (opens in a new tab)` : undefined}>
        {content}
      </a>
    );
  }
  return (
    <Link to={target.href} state={{ gameFilter: game.id }} className={className} aria-label={ariaLabel} onClick={() => window.scrollTo(0, 0)}>
      {content}
    </Link>
  );
};

/**
 * Ticket status + action for an upcoming game, so every card states clearly
 * whether Vipers tickets are being sold for it.
 */
export const GameTicketStatus: React.FC<{ game: GameTicketFields; ariaLabel?: string }> = ({ game, ariaLabel }) => {
  const target = getGameTicketTarget(game);
  if (target) {
    return (
      <div className="vb-ticket-status is-onsale">
        <span className="vb-ticket-status__label"><span className="vb-ticket-status__dot" aria-hidden="true" />Tickets on sale</span>
        <GetTicketsButton game={game} ariaLabel={ariaLabel} />
      </div>
    );
  }
  if (game.isHome) {
    return (
      <div className="vb-ticket-status is-pending">
        <span className="vb-ticket-status__label"><span className="vb-ticket-status__dot" aria-hidden="true" />Tickets not on sale yet</span>
        <span className="vb-ticket-status__sub">Check back soon</span>
      </div>
    );
  }
  return (
    <div className="vb-ticket-status is-away">
      <span className="vb-ticket-status__label"><span className="vb-ticket-status__dot" aria-hidden="true" />Host team tickets</span>
      <span className="vb-ticket-status__sub">Not sold on this site</span>
    </div>
  );
};
